import { groupBy, isEqual, sumBy } from 'lodash';
import lookup from 'country-code-lookup';
import { createSelector } from 'reselect';
import { endOfMonth, format, getISOWeek, getYear, isAfter, isSameYear, startOfMonth, subMonths, } from 'date-fns';
import getAccounts from 'utils/api/getAccounts';
import getListAccounts from 'utils/api/getListAccounts';
import getListActiveAccounts from 'utils/api/getListActiveAccounts';
import { extractManualAccounts } from 'utils/api/getManualAccounts';
import { isUnlocked } from 'features/premium/isUnlocked';
import { selectIsDefaultSpace, selectSelectedSpace, selectSelectedSpaceData, selectSelectedSpaceInfo, selectSpaces, } from 'features/spaces/reducers/selectors';
import { getBalance } from 'utils/api/getBalance';
import getCommittedTransactions, { getCommittedTransactionsForTrueBalance, } from 'utils/api/getCommittedTransactions';
import checkFeatureFlag from 'utils/featureFlags';
import { sortByName, strcmp } from 'utils/sort';
import { createNonMemoizedSelector } from 'store/selectors';
import i18n from 'utils/i18n';
import { formatAmount } from 'utils/formatting';
import { overviewColorMap } from 'features/accounts/constants';
import { AccountTypeDisplay } from 'features/invest/constants';
import buildCards from 'features/feed/components/quotes/buildCards';
import { validAccountNumber, validSortCode } from 'utils/validation';
import { isConnectionExpiring, isConnectionBroken, isConnectionWithConsentExpiresAt, } from 'features/settings/logins/utils';
import { isBankLoginConnection, isCreditReportAccount, isCreditReportConnection, } from 'utils/creditReportAccounts';
import { selectIsGBUser } from './selectors/user';
import { emptyArray, isWeb } from '../constants';
export const selectBalances = (state) => state.accounts.balances;
export const selectTags = (state) => state.transactions.tags;
export const selectDefaultCurrency = (store) => store.user.user.currency || 'GBP';
export const selectDefaultZeroAmount = createSelector(selectDefaultCurrency, (defaultCurrency) => ({
    amount: 0,
    currency: defaultCurrency,
}));
export const selectBeneficiaries = (state) => state.beneficiaries.beneficiaries;
export const selectFilteredBeneficiaries = createSelector([selectBeneficiaries, (_, props) => props], (beneficiaries, searchFilter) => {
    if (searchFilter === '') {
        return beneficiaries;
    }
    const regExp = new RegExp(searchFilter, 'i');
    return beneficiaries.filter((payee) => payee.name.match(regExp));
}, {
    devModeChecks: {
        identityFunctionCheck: 'never',
    },
});
export const selectUser = (state) => state.user.user;
export const selectUserId = (state) => state.user.user.id;
export const selectUserDefaultSpaceId = (state) => state.user.user.defaultSpaceId;
export const selectUserAdditionalInfo = (state) => state.user.userAdditionalInfo;
export const PHONE_NUMBER_VERIFICATION_MAX_AGE_MONTHS = 3;
export const selectPhoneNumberRecentlyVerified = (state) => {
    const { phoneNumber, phoneNumberUpdatedAt } = state.user.user;
    return !!(phoneNumber &&
        phoneNumberUpdatedAt &&
        isAfter(phoneNumberUpdatedAt, subMonths(new Date(), PHONE_NUMBER_VERIFICATION_MAX_AGE_MONTHS)));
};
export { selectIsGBUser };
export const selectUserPhotoUrl = (state) => state.user.user?.userImage?.url;
export const selectStatusMessage = (state) => state.user.statusMessage;
export const selectIsUserFetching = (state) => state.user.isFetching;
export const selectHasEmployerBenefit = (store) => Boolean(store.user.employerBenefitEligibility?.eligible);
export const selectHasStartedKYC = (store) => Boolean(store.user.user.kycStartedAt);
export const selectIsIdentityLocked = (store) => Boolean(store.user.user.kycStartedAt || store.user.user.creditScoreIdentityLockedAt);
export const selectGroups = (store) => store.groups.groups;
export const selectGroupById = createSelector([(store) => store.groups.groups, (_, id) => id], (groups, id) => groups.find((group) => group.id === id));
export const selectGroupTransactions = createSelector([(store) => store.groups.transactionsCache, (_, id) => id], (transactions, id) => transactions[id]);
export const selectGroupTransactionById = createSelector([selectGroupTransactions, (_store, _groupId, transactionId) => transactionId], (transactions, transactionId) => transactions?.find((t) => t.id === transactionId));
export const selectGroupAnalytics = createSelector([(store) => store.groups.analyticsCache, (_, id) => id], (analytics, id) => analytics[id]);
export const selectCurrentUserHasGroupSettlement = createSelector(selectGroupAnalytics, (analytics) => analytics?.users.some((user) => user.myBalance) ?? false);
export const selectGroupInfoForExtraMemberSuggestion = createSelector([
    selectGroupById,
    selectGroupAnalytics,
    (_, groupId) => groupId,
], (group, groupAnalytics, groupId) => {
    if (!groupId)
        return null;
    if (group && groupAnalytics) {
        if (groupAnalytics.users.length > 0) {
            return {
                groupName: group.name,
                users: groupAnalytics.users.filter((u) => !u.deleted && u.handle && u.id !== group.createdBy),
            };
        }
    }
    return null;
});
export const selectPrimaryAddress = createSelector((state) => state.addressReducer.userAddresses, (userAddresses) => userAddresses.find((item) => item.isPrimary));
/* Bill sharing */
export const selectBillSharingByTransactionId = (state, id) => state.sharedBills[id];
export const selectPendingBillSharingByTransactionId = createSelector(selectBillSharingByTransactionId, (sharedBills) => sharedBills?.filter((activity) => activity.status === 'NOT_YET_ACTIONED'));
/* Feed */
export const selectFeed = (state) => state.feed.feed;
/* Utils */
export const selectFeatureFlags = (state) => state.utils.featureFlags;
export const selectScrambled = (state) => state.utils.scrambled;
export const selectModalNotifications = (state) => state.utils.modalNotifications;
export const selectIsMyAccount = createSelector([
    selectIsDefaultSpace,
    selectUserId,
    (_, accountUserId) => accountUserId,
], (isDefaultSpace, myId, accountUserId) => isDefaultSpace || myId === accountUserId);
export const selectPendingSpaceInvites = createSelector([(store) => store.user.spaceInvites], (spaceInvites) => spaceInvites.filter((invite) => !invite.rejectedAt && !invite.acceptedAt && !invite.cancelledAt));
export const selectInviteWithNumberOfInvites = createSelector([selectPendingSpaceInvites], (invites) => {
    if (invites.length === 0) {
        return {
            invite: undefined,
            numInvites: 0,
        };
    }
    return {
        invite: invites[0],
        numInvites: invites.length,
    };
});
export const selectConnections = createSelector([(store) => store.user.connections], (connections) => (connections || []).map((connection) => ({
    ...connection,
    ...(isCreditReportConnection(connection) && {
        proPreviewAccounts: connection.proPreviewAccounts?.filter((account) => !account.isHidden),
    }),
    accounts: (isCreditReportConnection(connection)
        ? connection.accounts.filter((account) => !account.isHidden)
        : connection.accounts).map((account) => {
        if (account.bankConnectionId === connection.id) {
            return {
                ...account,
                isConnectionClosed: connection.isClosed,
                provider: connection.bankInfo.provider,
                iconUrl: account.iconUrl || connection.bankInfo.iconUrl,
                iconBase64: connection.bankInfo.iconBase64,
                statusMessage: connection.statusMessage,
                needsReauth: connection.needsReauth,
                needsReconsent: connection.needsReconsent,
                deactivatedOverQuota: connection.deactivatedOverQuota,
                lastSuccessfulSync: connection.lastSuccessfulSync,
                supportsVrp: connection.bankInfo.supportsVrp,
                bankName: connection.bankInfo.name,
            };
        }
        return account;
    }),
})));
export const selectActiveConnections = createSelector([selectConnections], (connections) => connections.filter((conn) => !conn.deactivatedOverQuota));
export const selectDeactivatedConnections = createSelector([selectConnections], (connections) => connections.filter((conn) => conn.deactivatedOverQuota));
export const selectConnectionsWithoutManual = createSelector([selectConnections], (connections) => connections.filter(isBankLoginConnection));
export const selectActiveConnectionsWithoutManual = createSelector([selectConnections], (connections) => connections.filter((conn) => isBankLoginConnection(conn) && !conn.deactivatedOverQuota));
export const selectActiveConnectionsWithAllAccountsClosed = createSelector([selectActiveConnectionsWithoutManual], (connections) => connections.filter((conn) => !conn.isClosed &&
    !conn.isSyncing &&
    !conn.isSyncingManual &&
    [
        ...conn.accounts,
        ...(conn.proPreviewAccounts ?? []),
        ...(conn.ultimatePreviewAccounts ?? []),
    ].every((account) => account.isClosed)));
export const selectBrokenConnections = createSelector([selectConnections], (connections) => connections.filter(isConnectionBroken));
export const selectConnectionById = createSelector([selectConnections, (_, connectionId) => connectionId], (connections, connectionId) => {
    const conn = connections.find(({ id }) => id === connectionId);
    if (conn) {
        return {
            ...conn,
            allAccounts: [
                ...conn.accounts,
                ...(conn.proPreviewAccounts ?? []),
                ...(conn.ultimatePreviewAccounts ?? []),
            ],
        };
    }
    return undefined;
});
export const selectSpaceTradingAccounts = (store) => store.invest.spaceTradingAccounts;
export const selectCurrentSpaceTradingAccounts = createSelector([(store) => store.invest.spaceTradingAccounts, selectSelectedSpace], (tradingAccounts, spaceId) => {
    if (spaceId && tradingAccounts && tradingAccounts[spaceId]?.length) {
        return tradingAccounts[spaceId];
    }
    return [];
});
export const selectUserTradingAccounts = createSelector([(store) => store.invest.accounts, selectUserId, selectSpaces], (tradingAccountObjects, userId, spaces) => {
    const defaultSpace = spaces.find((space) => space.isDefault);
    if (defaultSpace) {
        const accounts = [];
        if (tradingAccountObjects) {
            const activeAccountObjects = tradingAccountObjects.filter(({ account }) => account.status === 'ACTIVE');
            const balanceFallback = { value: 0, currency: 'GBP' };
            activeAccountObjects.forEach(({ account }) => {
                accounts.push({
                    id: account.id,
                    userId,
                    name: AccountTypeDisplay[account.accountType],
                    isEmmaInvestmentAccount: true,
                    spaceId: account.spaceId,
                    balance: (account.broker === 'seccl'
                        ? account.portfolioValue
                        : account.longPositionsMarketValue) ?? balanceFallback,
                    accountType: account.accountType,
                    broker: account.broker,
                });
            });
        }
        return accounts;
    }
    return undefined;
});
export const selectValidGBAccounts = createSelector([
    selectActiveConnectionsWithoutManual,
    (_, props) => props,
], (connections, props) => {
    const { filterBusiness, allowNonPISP, validateAccountNumberAndSortCode } = props || {};
    const accounts = [];
    for (let index = 0; index < connections.length; index++) {
        if (allowNonPISP || connections[index].bankInfo?.pispBankId) {
            for (let x = 0; x < connections[index].accounts.length; x++) {
                const { sortCode, accountNumber, isClosed, isBusiness, currency, nativeBalance, } = connections[index].accounts[x];
                const finalCurrency = nativeBalance?.currency || currency;
                if (sortCode &&
                    sortCode.length > 0 &&
                    accountNumber &&
                    finalCurrency === 'GBP' &&
                    accountNumber.length > 0 &&
                    // If we’re sending to it MUST have a valid account number and sort code
                    (!validateAccountNumberAndSortCode ||
                        (validAccountNumber(accountNumber) && validSortCode(sortCode))) &&
                    !isClosed &&
                    (filterBusiness ? !isBusiness : true)) {
                    const account = {
                        ...connections[index].accounts[x],
                        sortCode,
                        accountNumber,
                        bankId: connections[index].bankId,
                        bankName: connections[index].bankInfo.name,
                        lastSuccessfulSync: connections[index].lastSuccessfulSync,
                        pispBankId: connections[index].bankInfo?.pispBankId,
                    };
                    accounts.push(account);
                }
            }
        }
    }
    return accounts;
});
const allowNonPISPParams = { allowNonPISP: true };
export const selectGBAccountsWithPISPInfo = createSelector([(store) => selectValidGBAccounts(store, allowNonPISPParams)], (accounts) => {
    const supported = [];
    const notSupported = [];
    for (const account of accounts) {
        if (account.pispBankId != null && account.originalType === 'CHECKING') {
            supported.push(account);
        }
        else {
            notSupported.push(account);
        }
    }
    return {
        accounts: [...supported, ...notSupported],
        supported,
        notSupported,
        supportedCount: supported.length,
        notSupportedCount: notSupported.length,
        hasSupported: supported.length > 0,
    };
});
const selectorParams = {
    filterBusiness: true,
};
export const selectHasValidGBAccounts = createSelector([(store) => selectValidGBAccounts(store, selectorParams)], (accounts) => accounts.length > 0);
export const selectIsValidGBAccount = createSelector([
    (store) => selectValidGBAccounts(store, { validateAccountNumberAndSortCode: true }),
    (_, id) => id,
], (accounts, id) => !!accounts.find((account) => account.id === id));
const EMPTY_ACCOUNTS = [];
// Selects any accounts that don’t belong 'to me' in the space
const selectOtherSpaceAccounts = createSelector([selectSelectedSpaceInfo, selectUser], (spaceInfo, user) => {
    if (spaceInfo === undefined)
        return EMPTY_ACCOUNTS;
    return (spaceInfo?.accounts?.filter((acc) => user.id !== acc.userId) ||
        EMPTY_ACCOUNTS);
});
const selectMyAccountsInSpace = createSelector([selectSelectedSpaceInfo, selectUser], (spaceInfo, user) => {
    if (spaceInfo === undefined)
        return EMPTY_ACCOUNTS;
    return (spaceInfo?.accounts?.filter((acc) => user.id === acc.userId) ||
        EMPTY_ACCOUNTS);
});
/* User */
export const selectPasscode = (state) => state.user.passCode;
export const selectLastSession = (state) => state.user.lastSession;
/**
 * Prefer the use of use useRequiredTierCallback for callbacks
 */
export const selectIsUnlockedForSpace = createSelector([selectSelectedSpaceData, selectUser, (_state, tier) => tier], (spaceData, user, requiredTier) => requiredTier === undefined ||
    isUnlocked(user, requiredTier) ||
    spaceData?.isPremium === true);
export const selectAllMyAccountsFromConnections = createSelector([selectConnections, selectMyAccountsInSpace], (connections, spaceMyAccounts) => connections
    .map((connection) => connection.accounts.concat(
// If the user has these accounts in a premium space, then we can show them as unlocked premium accounts
connection.ultimatePreviewAccounts?.map((acc) => spaceMyAccounts.find((accInSpace) => accInSpace.id === acc.id)
    ? acc
    : {
        ...acc,
        availableBalance: 0,
        postedBalance: 0,
        ultimatePreviewAccount: true,
    }) || [], connection.proPreviewAccounts?.map((acc) => spaceMyAccounts.find((accInSpace) => accInSpace.id === acc.id)
    ? acc
    : {
        ...acc,
        availableBalance: 0,
        postedBalance: 0,
        proPreviewAccount: true,
    }) || []))
    .flat(1));
export const selectListAccounts = createSelector([
    selectAllMyAccountsFromConnections,
    selectSelectedSpace,
    selectOtherSpaceAccounts,
], (allAccountsFromConnections, selectedSpaceId, otherSpaceAccounts) => getListAccounts([
    { accounts: allAccountsFromConnections },
    { accounts: otherSpaceAccounts },
], selectedSpaceId).filter((acc) => !acc.deactivatedOverQuota &&
    !acc.proPreviewAccount &&
    !acc.ultimatePreviewAccount));
export const selectAccounts = createSelector([
    selectAllMyAccountsFromConnections,
    selectSelectedSpace,
    selectOtherSpaceAccounts,
], (allAccountsFromConnections, selectedSpaceId, otherSpaceAccounts) => getAccounts(getListAccounts([
    { accounts: allAccountsFromConnections },
    { accounts: otherSpaceAccounts },
], selectedSpaceId)));
// The new app separates debt from checking,
// we can get rid of this and modify the api.js function when we ditch old design
export const selectAccountsSeparatedDebt = createSelector([selectAccounts], (accounts) => ({
    ...accounts,
    CHECKING_CLOSED: {
        ...accounts.CHECKING_CLOSED,
        array: accounts.CHECKING_CLOSED.array.filter((b) => b.type !== 'CREDITCARD' && b.type !== 'LOAN'),
        positiveAccounts: accounts.CHECKING_CLOSED.positiveAccounts.filter((b) => b.type !== 'CREDITCARD' && b.type !== 'LOAN'),
        negativeAccounts: accounts.CHECKING_CLOSED.negativeAccounts.filter((b) => b.type !== 'CREDITCARD' && b.type !== 'LOAN'),
    },
    CHECKING_HIDDEN: {
        ...accounts.CHECKING_HIDDEN,
        array: accounts.CHECKING_HIDDEN.array.filter((b) => b.type !== 'CREDITCARD' && b.type !== 'LOAN'),
        positiveAccounts: accounts.CHECKING_CLOSED.positiveAccounts.filter((b) => b.type !== 'CREDITCARD' && b.type !== 'LOAN'),
        negativeAccounts: accounts.CHECKING_CLOSED.negativeAccounts.filter((b) => b.type !== 'CREDITCARD' && b.type !== 'LOAN'),
    },
}));
export const selectIsFitForDebtConsolidation = createSelector([selectAccounts], (accounts) => {
    if (accounts.CREDITCARD.array?.length) {
        let creditCardsWithBalance = 0;
        for (let i = 0; i < accounts.CREDITCARD.array.length; i++) {
            if ((accounts.CREDITCARD.array[i].creditCardBalance || 0) < 0 &&
                !accounts.CREDITCARD.array[i].isClosed) {
                creditCardsWithBalance += 1;
            }
            if (creditCardsWithBalance >= 2) {
                break;
            }
        }
        return creditCardsWithBalance >= 2;
    }
    return false;
});
const getCurrency = (data) => {
    const firstItem = data[0];
    if (firstItem) {
        if ('currency' in firstItem) {
            return firstItem.currency;
        }
        if (firstItem.balance?.currency) {
            return firstItem.balance.currency;
        }
    }
    return undefined;
};
export const selectAllSavingAccounts = createSelector((store) => store.pots.spacePots, selectSelectedSpace, selectAccounts, selectDefaultCurrency, (spacePots, spaceId, accounts, defaultCurrency) => {
    const pots = spaceId ? spacePots?.[spaceId] || [] : [];
    const array = [...(accounts?.SAVINGS?.array || []), ...pots];
    const positiveAccounts = [
        ...(accounts?.SAVINGS?.positiveAccounts || []),
        ...pots,
    ];
    const totalPotsAmount = pots.reduce((prev, curr) => prev + (curr.primaryCurrencyBalance?.value || curr.balance.value), 0);
    const total = accounts.SAVINGS.total + totalPotsAmount;
    const positiveTotal = accounts.SAVINGS.positiveTotal + totalPotsAmount;
    return {
        total,
        positiveTotal,
        array,
        positiveAccounts,
        hasPots: !!pots.length,
        currency: getCurrency(array) || defaultCurrency,
    };
});
export const selectAllInvestmentAccounts = createSelector((store) => store.invest.spaceTradingAccounts, selectSelectedSpace, selectAccounts, selectDefaultCurrency, (spaceTradingAccounts, spaceId, accounts, defaultCurrency) => {
    if (spaceId && spaceTradingAccounts) {
        const tradingAccounts = spaceTradingAccounts[spaceId] || [];
        const array = (() => {
            if (tradingAccounts.length) {
                return [...accounts.INVESTMENT.array, ...tradingAccounts];
            }
            return accounts.INVESTMENT.array;
        })();
        const { total } = accounts.INVESTMENT;
        const tradingTotal = tradingAccounts.reduce((prev, curr) => prev + (curr.balance?.value || 0), 0);
        return {
            array,
            total: total + tradingTotal,
            currency: getCurrency(array) || defaultCurrency,
        };
    }
    return {
        ...accounts.INVESTMENT,
        currency: getCurrency(accounts.INVESTMENT.array) || defaultCurrency,
    };
});
export const selectAllInvestmentWithCryptoAccounts = createSelector((store) => store.invest.spaceTradingAccounts, selectSelectedSpace, selectAccounts, selectDefaultCurrency, (spaceTradingAccounts, spaceId, accounts, defaultCurrency) => {
    if (spaceId && spaceTradingAccounts) {
        const tradingAccounts = spaceTradingAccounts[spaceId] || [];
        const array = (() => {
            if (tradingAccounts.length) {
                return [...accounts.ALL_INVESTMENT.array, ...tradingAccounts];
            }
            return accounts.ALL_INVESTMENT.array;
        })();
        const positiveAccounts = (() => {
            if (tradingAccounts.length) {
                return [
                    ...accounts.ALL_INVESTMENT.positiveAccounts,
                    ...tradingAccounts,
                ];
            }
            return accounts.ALL_INVESTMENT.positiveAccounts;
        })();
        const { total, positiveTotal } = accounts.ALL_INVESTMENT;
        const tradingTotal = tradingAccounts.reduce((prev, curr) => prev + (curr.balance?.value || 0), 0);
        return {
            array,
            positiveAccounts,
            positiveTotal: positiveTotal + tradingTotal,
            total: total + tradingTotal,
            currency: getCurrency(array) || defaultCurrency,
        };
    }
    return {
        ...accounts.ALL_INVESTMENT,
        currency: getCurrency(accounts.ALL_INVESTMENT.array) || defaultCurrency,
    };
});
// both personal and business, not emma wallet, not manual
// need to include all accounts from all spaces
// right now for notifications only
export const selectAllCurrentAndCreditCardAccounts = createSelector([selectConnections, selectDefaultCurrency], (connections, defaultCurrency) => {
    const array = connections
        .flatMap((connection) => connection.accounts)
        .filter((account) => !account.deactivatedOverQuota &&
        (account.type === 'CHECKING' || account.type === 'CREDITCARD') &&
        account.provider !== 'MANUAL');
    return {
        array,
        currency: getCurrency(array) || defaultCurrency,
    };
});
export const selectAllCurrentAccounts = createSelector([selectAccountsSeparatedDebt, selectDefaultCurrency], (accounts, defaultCurrency) => ({
    ...accounts.CHECKING,
    currency: getCurrency(accounts.CHECKING.array) || defaultCurrency,
}));
export const selectHasExpiringConnection = createSelector([selectConnections], (connections) => connections?.some(isConnectionExpiring) ?? false);
export const selectConnectionsWithConsentExpiresAt = createSelector([selectConnections], (connections) => connections?.filter(isConnectionWithConsentExpiresAt) ||
    emptyArray);
export const selectOpenConnectionsWithConsentExpiresAt = createSelector([selectConnections], (connections) => connections.filter((connection) => isConnectionWithConsentExpiresAt(connection) && !connection.isClosed));
export const selectIsSyncingManual = (store) => store.user.isSyncingManual;
export const selectListAccountsFromConnections = createSelector([selectAllMyAccountsFromConnections], (allAccounts) => getListAccounts([{ accounts: allAccounts }]));
const getActionRequired = ({ isClosed, isRemoved, isDeleted, needsReauth, needsReconsent, isConnectionClosed, directDebitCancelled, deactivatedOverQuota, directDebitAccountChanged, destinationsUnavailable, }) => {
    if (destinationsUnavailable) {
        return 'DESTINATIONS_UNAVAILABLE';
    }
    if (directDebitCancelled) {
        if (directDebitAccountChanged) {
            return 'DIRECT_DEBIT_ACCOUNT_CHANGED';
        }
        return 'DIRECT_DEBIT_CANCELLED';
    }
    if (isDeleted) {
        return 'DELETED';
    }
    if (deactivatedOverQuota) {
        return 'DEACTIVATED_OVER_QUOTA';
    }
    if (needsReauth) {
        return 'NEEDS_REAUTH';
    }
    if (needsReconsent) {
        return 'NEEDS_RECONSENT';
    }
    if (isRemoved) {
        return 'REMOVED';
    }
    if (isConnectionClosed) {
        return 'CONNECTION_CLOSED';
    }
    if (isClosed) {
        return 'CLOSED';
    }
    if (destinationsUnavailable) {
        return 'DESTINATIONS_UNAVAILABLE';
    }
    return undefined;
};
export const selectAutosaveStatusObj = createSelector((state) => state.pots.savingsPlan, (state) => state.invest.connectedAccount, selectListAccountsFromConnections, (savingsPlan, connectedAccount, accounts) => {
    if (savingsPlan) {
        const savingsPlanAccount = accounts.find((account) => account.id === savingsPlan.bankInfo?.accountId);
        if (savingsPlanAccount) {
            return {
                account: savingsPlanAccount,
                isPaused: savingsPlan.isPaused,
                isRemoved: savingsPlanAccount.isRemoved,
                isPending: savingsPlan.mandateStatus === 'PENDING',
                actionRequired: getActionRequired({
                    isDeleted: savingsPlan.bankInfo.isDeleted,
                    deactivatedOverQuota: savingsPlanAccount.deactivatedOverQuota,
                    directDebitCancelled: savingsPlan.mandateStatus === 'INACTIVE',
                    destinationsUnavailable: savingsPlan.depositStatus === 'NO_SUPPORTED_DESTINATIONS',
                    isClosed: savingsPlanAccount.isClosed,
                    isRemoved: savingsPlanAccount.isRemoved,
                    needsReauth: savingsPlanAccount.needsReauth,
                    needsReconsent: savingsPlanAccount.needsReconsent,
                    isConnectionClosed: savingsPlanAccount.isConnectionClosed,
                    directDebitAccountChanged: connectedAccount
                        ? savingsPlanAccount.sortCode?.replace(/\D/g, '') !==
                            connectedAccount?.ukSortCode?.replace(/\D/g, '') ||
                            savingsPlanAccount.accountNumber !==
                                connectedAccount?.ukAccountNumber
                        : false,
                }),
            };
        }
        return {
            account: undefined,
            isPaused: savingsPlan.isPaused,
            isPending: savingsPlan.mandateStatus === 'PENDING',
            actionRequired: getActionRequired({
                isDeleted: savingsPlan.bankInfo.isDeleted,
                directDebitCancelled: savingsPlan.mandateStatus === 'INACTIVE',
                destinationsUnavailable: savingsPlan.depositStatus === 'NO_SUPPORTED_DESTINATIONS',
            }),
        };
    }
    return undefined;
}, {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
export const selectAccountById = createSelector([selectListAccounts, (_, id) => id], (accounts, id) => id !== undefined ? accounts.find((account) => account.id === id) : undefined);
export const selectAllAccountsMap = createSelector([selectListAccountsFromConnections], (accounts) => accounts.reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {}));
export const selectAccountByAccountNumber = createSelector([
    selectListAccountsFromConnections,
    (_, accountNumber) => accountNumber,
], (accounts, accountNumber) => accounts.find((account) => account.accountNumber === accountNumber), {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => isEqual(a, b),
    },
});
export const selectActiveAccountsWithHidden = createSelector([selectListAccounts, selectSelectedSpace], (accounts, selectedSpaceId) => getListActiveAccounts(accounts, selectedSpaceId, true).filter((account) => !isCreditReportAccount(account)));
/** Accounts that can hold transactions, for the transactions account filter */
export const selectTransactionFilterAccounts = createSelector([selectListAccounts], (accounts) => accounts.filter((account) => !isCreditReportAccount(account)));
export const selectActiveManualAccounts = createSelector([selectConnections, selectSelectedSpace, selectOtherSpaceAccounts], (connections, selectedSpaceId, otherSpaceAccounts) => extractManualAccounts([...connections, { accounts: otherSpaceAccounts }], false, selectedSpaceId));
export const selectClosedManualAccounts = createSelector([selectConnections, selectSelectedSpace, selectOtherSpaceAccounts], (connections, selectedSpaceId, otherSpaceAccounts) => extractManualAccounts([...connections, { accounts: otherSpaceAccounts }], true, selectedSpaceId));
export const selectAllManualAccountsCreatedByUser = createSelector([selectAllAccountsMap], (accounts) => Object.values(accounts).filter((account) => account.provider === 'MANUAL'));
export const selectOrderedCategories = createSelector([(store) => store.user.categories], (categories) => sortByName(categories));
export const selectOrderedCustomCategories = createSelector([(store) => store.user.customCategories], (categories) => sortByName(categories));
export const selectOrderedParentCategories = createSelector([(store) => store.user.customCategories], (categories) => sortByName(categories.filter((category) => !category.parentCategoryId)));
export const selectOrderedSubcategories = createSelector([(store) => store.user.customCategories], (categories) => sortByName(categories.filter((category) => !!category.parentCategoryId)));
const sortByPriority = (data) => [...data].sort((a, b) => {
    const pa = a.priority ?? 0;
    const pb = b.priority ?? 0;
    if (pa !== pb)
        return pa - pb;
    return a.displayName.localeCompare(b.displayName);
});
export const selectAllCategories = createSelector([
    (store) => store.user.categories,
    (store) => store.user.customCategories,
], (categories, customCategories) => {
    if (!categories || !customCategories) {
        if (!categories && !customCategories) {
            return [];
        }
        if (!categories) {
            return sortByPriority(customCategories);
        }
        if (!customCategories) {
            return sortByPriority(categories);
        }
    }
    return sortByPriority([...categories, ...customCategories]);
});
export const selectExcludedCategoriesCount = createSelector([selectAllCategories], (categories) => categories.reduce((acc, curr) => acc + (curr.hideFromStats && !curr.parentCategoryId ? 1 : 0), 0));
/**
 * Categories hidden from stats, including subcategories of a hidden parent.
 */
export const selectExcludedCategoryIds = createSelector([selectAllCategories], (categories) => {
    const excludedParentIds = new Set(categories
        .filter((category) => category.hideFromStats && !category.parentCategoryId)
        .map((category) => category.id));
    return new Set(categories
        .filter((category) => category.hideFromStats ||
        (!!category.parentCategoryId &&
            excludedParentIds.has(category.parentCategoryId)))
        .map((category) => category.id));
});
export const selectNormalizedCategories = createSelector([selectAllCategories], (categories) => categories.reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {}));
export const selectNormalizedCategoriesWithTransactionsCount = createSelector([selectNormalizedCategories, (store) => store.transactions.categories], (normalizedCategories, transactionCategories) => {
    const categories = {};
    Object.values(normalizedCategories).forEach((category) => {
        const transactionsCount = transactionCategories[category.id]?.transactionsCount;
        if (!transactionsCount)
            return;
        categories[category.id] = {
            ...normalizedCategories[category.id],
            transactionsCount,
        };
    });
    return categories;
});
export const selectParentCategoriesForKeyboardCategories = createSelector([selectNormalizedCategoriesWithTransactionsCount], (parentCategories) => Object.values(parentCategories).filter((category) => !!category && !category.parentCategoryId));
export const selectCategory = createSelector([selectAllCategories, (_, categoryId) => categoryId], (categories, categoryId) => {
    if (!categoryId)
        return null;
    return categories.find((category) => category.id === categoryId);
});
export const selectParentCategories = createSelector([selectAllCategories], (categories) => categories.filter((category) => !category.parentCategoryId));
export const selectParentCategoriesWithSubcategories = createSelector([selectParentCategories, selectNormalizedCategories], (parentCategories, normalizedCategories) => parentCategories.map((category) => ({
    ...category,
    subcategories: category.subCategoryIds
        ?.map((id) => normalizedCategories[id])
        .filter(Boolean),
})));
export const selectVisibleParentCategories = createSelector([selectParentCategories], (categories) => categories.filter((category) => category.priority !== -1));
export const selectSubcategories = createSelector([selectAllCategories], (categories) => categories.filter((category) => !!category.parentCategoryId));
export const selectHasSubcategories = createSelector([selectSubcategories], (subcategories) => subcategories.length > 0);
export const selectCategoryHasSubcategories = createSelector([selectNormalizedCategories, (_, categoryId) => categoryId], (normalizedCategories, categoryId) => {
    if (!categoryId)
        return undefined;
    return !!normalizedCategories[categoryId]?.subCategoryIds?.length;
});
export const selectSubcategoriesByParentCategoryId = createSelector([
    selectSubcategories,
    (_, parentCategoryId) => parentCategoryId,
], (subcategories, parentCategoryId) => {
    if (!parentCategoryId)
        return [];
    return subcategories.filter((category) => category.parentCategoryId === parentCategoryId);
});
export const selectMerchants = createSelector([(store) => store.transactions.merchants], (merchants) => Object.entries(merchants).map(([key, value]) => ({
    ...value,
    id: Number(key),
})));
export const selectFilteredMerchants = createSelector([selectMerchants, (_, sortMethod) => sortMethod], (merchants, sortMethod) => {
    switch (sortMethod) {
        case 'Alphabetical':
            return [...merchants].sort((a, b) => a.displayName.localeCompare(b.displayName));
        case 'Frequency':
            return [...merchants].sort((a, b) => (b?.transactionsCount || 0) - (a?.transactionsCount || 0));
        default: {
            return merchants;
        }
    }
});
export const selectCommittedTransactions = createSelector((store) => store.subscriptions.committed?.subscriptions ||
    emptyArray, (committed) => getCommittedTransactions(committed.filter((subscription) => !subscription.isIncome)));
const selectCommittedForRange = (store, from, until) => {
    const { committed, committedForRange } = store.subscriptions;
    const currentPaydayRange = selectCurrentPaydayRangeWithFallback(store);
    if (from === currentPaydayRange.from && until === currentPaydayRange.to) {
        return committed;
    }
    if (committedForRange?.from === from && committedForRange.until === until) {
        return committedForRange;
    }
    return undefined;
};
/**
 * The committed result for a period, or undefined until it has been fetched.
 * The current pay period reads the same result True balance uses.
 */
export const selectCommittedTransactionsForRange = createSelector([selectCommittedForRange], (committed) => ({
    committed,
    transactions: getCommittedTransactions((committed?.subscriptions || emptyArray).filter((subscription) => !subscription.isIncome)),
}));
export const selectCommittedTransactionsForAccount = createSelector([
    (store) => store.subscriptions.committed?.subscriptions,
    (_, account) => account,
], (committed, account) => {
    const transactions = committed?.filter((item) => item.accountId === account?.id);
    return getCommittedTransactionsForTrueBalance(transactions || [], true);
});
export const selectBalancesForAccount = createSelector([
    selectCommittedTransactionsForAccount,
    (_, account) => account,
], (committed, account) => {
    const { nativeBalance, currency, availableBalance, postedBalance } = account;
    const mainCurrency = nativeBalance ? nativeBalance.currency : currency;
    let mainBalance = typeof availableBalance === 'number' ? availableBalance : postedBalance;
    if (nativeBalance)
        mainBalance =
            typeof nativeBalance.availableBalance === 'number'
                ? nativeBalance.availableBalance
                : nativeBalance.postedBalance;
    return {
        balance: mainBalance,
        currency: mainCurrency,
        upcomingBalance: committed.totalToBePaid,
        upcomingIncome: committed.totalIncomeToBeReceived,
        trueBalance: mainBalance -
            committed.totalToBePaid +
            committed.totalIncomeToBeReceived,
    };
});
export const selectCurrentPaydayRange = createSelector([selectUser, selectSelectedSpaceData], (user, spaceData) => {
    if (spaceData && !spaceData.isDefault) {
        return spaceData.currentPaydayRange || undefined;
    }
    if (user?.currentPaydayRange) {
        return user.currentPaydayRange;
    }
    return undefined;
});
export const selectHasSetPaydayRange = createSelector([selectCurrentPaydayRange], (currentPaydayRange) => currentPaydayRange !== undefined);
// A user may not have set a custom pay period BUT has set a total budget
export const selectHasSetBudget = createSelector([selectFeed], (feed) => Boolean(feed.cards.thisMonth?.budget != null));
export const selectCurrentPaydayRangeWithFallback = createSelector([selectCurrentPaydayRange], (currentPaydayRange) => {
    if (currentPaydayRange) {
        return currentPaydayRange;
    }
    return {
        from: startOfMonth(new Date()).toISOString(),
        to: endOfMonth(new Date()).toISOString(),
    };
}, {
    devModeChecks: {
        identityFunctionCheck: 'never',
    },
});
export const selectIsLoggedIn = (store) => store.user.isLoggedIn || isWeb;
export const selectPasscodeVisible = (store) => store.user.isPasscodeOn;
export const selectBiometricsEnabled = (store) => store.user.isTouchActive;
export const selectIsResettingPasscode = (store) => store.user.isResettingPasscode;
export const selectResetPasscodeNextStep = (store) => store.user.resetPasscodeNextStep;
export const selectColorSchemePreference = (store) => store.utils.colorScheme;
export const selectCurrentScreen = (store) => store.utils.currentScreen;
export const selectPreviousScreen = (store) => store.utils.previousScreen;
export const selectEnergyResults = (store) => store.switching.energyResults;
/** Quiz */
export const selectQuiz = (store) => store.quiz.quiz;
export const selectDidOnboardNetworth = (store) => store.deviceSettings.didOnboardNetworth;
// The field "rating" is incremented every time the user opens Feed
export const selectSessionCount = (store) => store.user.rating;
export const selectLastReviewSessionCount = (store) => store.deviceSettings?.lastReviewSessionCount;
/**
 * Balance history selectors start
 */
const buildAverageArray = (arrayItems) => {
    if (!arrayItems) {
        return [];
    }
    const averageItems = Object.keys(arrayItems).map((key) => {
        const item = arrayItems[key];
        const displayStart = format(new Date(item[item.length - 1].timestamp), // timestamp is in format 2010/1/1, not ISO
        'do MMM yyyy');
        const displayEnd = format(new Date(item[0].timestamp), 'do MMM yyyy');
        let averageBalance = sumBy(item, 'balance') / item.length;
        averageBalance = parseFloat(averageBalance.toFixed(2));
        return {
            timestamp: item[0].timestamp,
            balance: averageBalance,
            currency: item[0].currency,
            label: `${displayStart} - ${displayEnd}`, // eg: '01 Jan 2020 -  7 Jan 2020'
        };
    });
    return averageItems.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
};
export const formatChartTimestamp = (timestamp, currentYear) => {
    const dateObj = new Date(timestamp);
    if (dateObj.getFullYear() === currentYear) {
        return format(dateObj, 'EEE, d LLLL');
    }
    return format(dateObj, 'EEE, d LLLL yyyy');
};
export const formatToPeriods = (balanceHistory) => {
    const all = (() => {
        const balanceHistoryDup = [...balanceHistory].reverse();
        const firstIndexOfNonZeroBalance = balanceHistoryDup.findIndex((balanceHistory) => balanceHistory.balance !== 0);
        if (firstIndexOfNonZeroBalance >= 1460) {
            return undefined;
        }
        const groupedDaysByWeek = groupBy(balanceHistory, (item) => {
            const date = new Date(item.timestamp);
            return `${getISOWeek(date)}${getYear(date)}`;
        });
        const averageWeeks = buildAverageArray(groupedDaysByWeek);
        return averageWeeks;
    })();
    const today = new Date();
    const currentYear = today.getFullYear();
    const ytd = [];
    const updatedBalanceHistories = balanceHistory
        .slice(0, 365)
        .reverse()
        .map((balanceHistory) => {
        const item = {
            ...balanceHistory,
            formattedPrice: formatAmount(balanceHistory.balance, balanceHistory.currency, 2),
            label: formatChartTimestamp(balanceHistory.timestamp, currentYear),
            formattedAssetAmount: 'assetBalance' in balanceHistory &&
                typeof balanceHistory.assetBalance === 'number'
                ? formatAmount(balanceHistory.assetBalance, balanceHistory.currency, 2)
                : undefined,
            formattedDebtAmount: 'debtBalance' in balanceHistory &&
                typeof balanceHistory.debtBalance === 'number'
                ? formatAmount(balanceHistory.debtBalance, balanceHistory.currency, 2)
                : undefined,
            networthAmount: 'networthBalance' in balanceHistory &&
                typeof balanceHistory.networthBalance === 'number'
                ? balanceHistory.networthBalance
                : undefined,
        };
        if (isSameYear(balanceHistory.timestamp, today)) {
            ytd.push(item);
        }
        return item;
    });
    const year = updatedBalanceHistories;
    const sixmonths = year.slice(365 - 180);
    const threemonths = sixmonths.slice(180 - 90);
    const month = threemonths.slice(90 - 30);
    const week = month.slice(30 - 7);
    const lastUpdated = week.slice(7 - 1);
    return {
        day: [],
        lastUpdated,
        week,
        month,
        threemonths,
        sixmonths,
        year,
        ytd,
        all: all ?? year,
    };
};
const formattedEmptyPeriods = formatToPeriods([]);
export const selectSavingsBalance = createSelector([
    (state) => state.balanceHistory.pots,
    (state) => state.balanceHistory.savings,
    selectDefaultCurrency,
], (potsBalanceHistory, savingsBalanceHistory, defaultCurrency) => {
    if (!savingsBalanceHistory.length) {
        return formattedEmptyPeriods;
    }
    const balanceHistory = [];
    if (potsBalanceHistory.length &&
        potsBalanceHistory.length === savingsBalanceHistory.length) {
        if (potsBalanceHistory[0].timestamp.substring(0, 10) !==
            savingsBalanceHistory[0].timestamp.substring(0, 10)) {
            return formattedEmptyPeriods;
        }
        for (let i = 0; i < potsBalanceHistory.length; i++) {
            balanceHistory.push({
                currency: defaultCurrency,
                timestamp: potsBalanceHistory[i].timestamp,
                balance: potsBalanceHistory[i].balance + savingsBalanceHistory[i].balance,
            });
        }
    }
    return formatToPeriods(balanceHistory);
});
// the backend only sends the breakdown keys of account types the user holds, so
// a section missing from a point means the user has none of those accounts
export const debtBreakdownSections = [
    'current',
    'creditCard',
    'loans',
    'mortgages',
];
const formatDebtBreakdown = (breakdown, currency) => {
    if (!debtBreakdownSections.some((section) => typeof breakdown?.[section] === 'number')) {
        return undefined;
    }
    return {
        current: formatAmount(breakdown?.current || 0, currency, 2),
        creditCard: formatAmount(breakdown?.creditCard || 0, currency, 2),
        loans: formatAmount(breakdown?.loans || 0, currency, 2),
        mortgages: formatAmount(breakdown?.mortgages || 0, currency, 2),
    };
};
export const selectDebtBalanceHistory = createSelector((state) => state.balanceHistory.debt, (debtBalanceHistory) => formatToPeriods(debtBalanceHistory?.map((balanceHistory) => ({
    ...balanceHistory,
    breakdown: formatDebtBreakdown(balanceHistory.breakdown, balanceHistory.currency),
})) || []));
const hasBreakdownSection = (histories, section) => histories.some((history) => history?.some((item) => !!item.breakdown?.[section]));
// which sections of the debt history hold any debt, deciding which rows the debt
// chart tooltip shows. With a single section the tooltip would only repeat the
// total, so it is hidden unless at least two sections have debt
export const selectDebtBreakdownSections = createSelector([
    (state) => state.balanceHistory.debt,
    (state) => state.balanceHistory.debtAll,
], (debtBalanceHistory, allDebtBalanceHistory) => {
    const histories = [debtBalanceHistory, allDebtBalanceHistory];
    const hasSection = (section) => hasBreakdownSection(histories, section);
    return {
        current: hasSection('current'),
        loans: hasSection('loans'),
        mortgages: hasSection('mortgages'),
        creditCard: hasSection('creditCard'),
    };
});
export const selectAssetBreakdownSections = createSelector([
    (state) => state.balanceHistory.pots,
    (state) => state.balanceHistory.assets,
    (state) => state.balanceHistory.portfolio,
    (state) => state.balanceHistory.assetAll,
], (potsBalanceHistory, assetsBalanceHistory, portfolioBalanceHistory, allAssetBalanceHistory) => {
    const histories = [
        potsBalanceHistory,
        assetsBalanceHistory,
        portfolioBalanceHistory,
        allAssetBalanceHistory,
    ];
    const hasSection = (section) => hasBreakdownSection(histories, section);
    return {
        current: hasSection('current'),
        savings: hasSection('savings'),
        investment: hasSection('investment'),
        other: hasSection('other'),
    };
});
export const selectEverydayBreakdownSections = createSelector([
    (state) => state.balanceHistory.checking,
    (state) => state.balanceHistory.everydayAll,
], (checkingBalanceHistory, allEverydayBalanceHistory) => {
    const histories = [checkingBalanceHistory, allEverydayBalanceHistory];
    const hasSection = (section) => hasBreakdownSection(histories, section);
    return {
        current: hasSection('current'),
        creditCard: hasSection('creditCard'),
    };
});
export const selectNetWorthBalance = createSelector([
    (state) => state.balanceHistory.pots,
    (state) => state.balanceHistory.networth,
    (state) => state.balanceHistory.assets,
    (state) => state.balanceHistory.debt,
    (state) => state.balanceHistory.portfolio,
    selectDefaultCurrency,
], (potsBalanceHistory, networthBalanceHistory, assetsBalanceHistory, debtBalanceHistory, portfolioBalanceHistory, defaultCurrency) => {
    if (!networthBalanceHistory.length ||
        !assetsBalanceHistory?.length ||
        !debtBalanceHistory?.length) {
        return formattedEmptyPeriods;
    }
    const balanceHistory = [];
    if (networthBalanceHistory.length === assetsBalanceHistory.length &&
        networthBalanceHistory.length === debtBalanceHistory.length &&
        potsBalanceHistory.length === networthBalanceHistory.length &&
        portfolioBalanceHistory.length === networthBalanceHistory.length) {
        if (!(potsBalanceHistory[0].timestamp.substring(0, 10) ===
            assetsBalanceHistory[0].timestamp.substring(0, 10) &&
            potsBalanceHistory[0].timestamp.substring(0, 10) ===
                debtBalanceHistory[0].timestamp.substring(0, 10) &&
            potsBalanceHistory[0].timestamp.substring(0, 10) ===
                networthBalanceHistory[0].timestamp.substring(0, 10) &&
            portfolioBalanceHistory[0].timestamp.substring(0, 10) ===
                networthBalanceHistory[0].timestamp.substring(0, 10))) {
            return formattedEmptyPeriods;
        }
        for (let i = 0; i < networthBalanceHistory.length; i++) {
            balanceHistory.push({
                currency: defaultCurrency,
                timestamp: networthBalanceHistory[i].timestamp,
                balance: potsBalanceHistory[i].balance +
                    networthBalanceHistory[i].balance +
                    portfolioBalanceHistory[i].balance,
                assetBalance: potsBalanceHistory[i].balance +
                    assetsBalanceHistory[i].balance +
                    portfolioBalanceHistory[i].balance,
                debtBalance: debtBalanceHistory[i].balance,
            });
        }
    }
    return formatToPeriods(balanceHistory);
});
export const selectAssetsBalance = createSelector([
    (state) => state.balanceHistory.pots,
    (state) => state.balanceHistory.debt,
    (state) => state.balanceHistory.assets,
    (state) => state.balanceHistory.networth,
    (state) => state.balanceHistory.portfolio,
    selectDefaultCurrency,
], (potsBalanceHistory, debtBalanceHistory, assetsBalanceHistory, networthBalanceHistory, portfolioBalanceHistory, defaultCurrency) => {
    if (!networthBalanceHistory.length ||
        !assetsBalanceHistory?.length ||
        !debtBalanceHistory?.length) {
        return formattedEmptyPeriods;
    }
    const balanceHistory = [];
    if (networthBalanceHistory.length === assetsBalanceHistory.length &&
        networthBalanceHistory.length === debtBalanceHistory.length &&
        potsBalanceHistory.length === assetsBalanceHistory.length &&
        portfolioBalanceHistory.length === assetsBalanceHistory.length) {
        if (!(potsBalanceHistory[0].timestamp.substring(0, 10) ===
            networthBalanceHistory[0].timestamp.substring(0, 10) &&
            potsBalanceHistory[0].timestamp.substring(0, 10) ===
                debtBalanceHistory[0].timestamp.substring(0, 10) &&
            potsBalanceHistory[0].timestamp.substring(0, 10) ===
                assetsBalanceHistory[0].timestamp.substring(0, 10) &&
            portfolioBalanceHistory[0].timestamp.substring(0, 10) ===
                assetsBalanceHistory[0].timestamp.substring(0, 10))) {
            return formattedEmptyPeriods;
        }
        for (let i = 0; i < assetsBalanceHistory.length; i++) {
            const assetBalance = potsBalanceHistory[i].balance +
                assetsBalanceHistory[i].balance +
                portfolioBalanceHistory[i].balance;
            const finalBreakdown = {
                current: (potsBalanceHistory[i].breakdown?.current || 0) +
                    (assetsBalanceHistory[i].breakdown?.current || 0) +
                    (portfolioBalanceHistory[i].breakdown?.current || 0),
                savings: (potsBalanceHistory[i].breakdown?.savings || 0) +
                    (assetsBalanceHistory[i].breakdown?.savings || 0) +
                    (portfolioBalanceHistory[i].breakdown?.savings || 0),
                investment: (potsBalanceHistory[i].breakdown?.investment || 0) +
                    (assetsBalanceHistory[i].breakdown?.investment || 0) +
                    (portfolioBalanceHistory[i].breakdown?.investment || 0),
                other: (potsBalanceHistory[i].breakdown?.other || 0) +
                    (assetsBalanceHistory[i].breakdown?.other || 0) +
                    (portfolioBalanceHistory[i].breakdown?.other || 0),
            };
            balanceHistory.push({
                currency: defaultCurrency,
                timestamp: assetsBalanceHistory[i].timestamp,
                balance: assetBalance,
                assetBalance,
                debtBalance: debtBalanceHistory[i].balance,
                networthBalance: potsBalanceHistory[i].balance +
                    networthBalanceHistory[i].balance +
                    portfolioBalanceHistory[i].balance,
                breakdown: finalBreakdown.current ||
                    finalBreakdown.savings ||
                    finalBreakdown.investment ||
                    finalBreakdown.other
                    ? {
                        current: formatAmount(finalBreakdown.current || 0, assetsBalanceHistory[i].currency, 2),
                        investment: formatAmount(finalBreakdown.investment || 0, assetsBalanceHistory[i].currency, 2),
                        savings: formatAmount(finalBreakdown.savings || 0, assetsBalanceHistory[i].currency, 2),
                        other: formatAmount(finalBreakdown.other || 0, assetsBalanceHistory[i].currency, 2),
                    }
                    : undefined,
            });
        }
    }
    return formatToPeriods(balanceHistory);
});
export const selectInvestmentBalance = createSelector([
    (state) => state.balanceHistory.portfolio,
    (state) => state.balanceHistory.investment,
    selectDefaultCurrency,
], (portfolioBalanceHistory, investmentBalanceHistory, defaultCurrency) => {
    if (!investmentBalanceHistory.length) {
        return formattedEmptyPeriods;
    }
    const balanceHistory = [];
    if (portfolioBalanceHistory.length &&
        portfolioBalanceHistory.length === investmentBalanceHistory.length) {
        if (portfolioBalanceHistory[0].timestamp.substring(0, 10) !==
            investmentBalanceHistory[0].timestamp.substring(0, 10)) {
            return formattedEmptyPeriods;
        }
        for (let i = 0; i < investmentBalanceHistory.length; i++) {
            balanceHistory.push({
                currency: defaultCurrency,
                timestamp: investmentBalanceHistory[i].timestamp,
                balance: investmentBalanceHistory[i].balance +
                    portfolioBalanceHistory[i].balance,
            });
        }
    }
    return formatToPeriods(balanceHistory);
});
export const selectCheckingBalance = createSelector([(state) => state.balanceHistory.checking], (checkingBalanceHistory) => {
    if (!checkingBalanceHistory?.length) {
        return formattedEmptyPeriods;
    }
    const balanceHistory = checkingBalanceHistory.map((item) => {
        const hasBreakdown = typeof item.breakdown?.current === 'number' &&
            typeof item.breakdown?.creditCard === 'number';
        return {
            currency: item.currency,
            timestamp: item.timestamp,
            balance: item.balance,
            breakdown: hasBreakdown
                ? {
                    current: formatAmount(item.breakdown?.current || 0, item.currency, 2),
                    creditCard: formatAmount(item.breakdown?.creditCard || 0, item.currency, 2),
                }
                : undefined,
        };
    });
    return formatToPeriods(balanceHistory);
});
/**
 * Balance history selectors end
 */
export const selectBillingHistoryFirstThree = createSelector((state) => state.billingHistory.billingHistory, (history) => {
    const billingHistory = history?.slice(0, 3);
    return {
        billingHistory,
        mayHaveMore: !!(billingHistory?.length === 3),
        billingHistoryCount: billingHistory?.length || 0,
    };
});
export const selectBillingHistoryInitialLoading = createSelector((state) => state.billingHistory.requestingHistory, (state) => state.billingHistory.page, (requesting, page) => !!(requesting && page === 1));
export const selectFeatureFlag = createSelector([
    (store) => store.utils.featureFlags,
    (_, flag) => flag,
], (featureFlags, flag) => ({
    value: checkFeatureFlag(featureFlags, flag),
    extra: featureFlags[flag]?.extra,
}), {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => a.value === b.value && a.extra === b.extra,
    },
});
export const selectFeatureFlagVariant = createSelector([
    (store) => store.utils.featureFlags,
    (_, flag) => flag,
], (featureFlags, flag) => ({
    value: featureFlags[flag]?.value,
    extra: featureFlags[flag]?.extra,
}), {
    memoizeOptions: {
        resultEqualityCheck: (a, b) => a.value === b.value && a.extra === b.extra,
    },
});
export const selectIsChangeOfAddress = createSelector((state) => state.addressReducer.userAddresses, (addresses) => !!addresses.find((address) => address.kycStatus === 'SUCCESS'));
export const selectIsFetchingUserAddresses = (state) => state.addressReducer.isFetchingUserAddresses;
export const getDisplayAddressGB = (addresses) => {
    /**
     * This is the address with isPrimary
     */
    let primaryAddress;
    /**
     * This is the address with KYC status
     */
    let addressWithKYCStatus;
    for (let i = 0; i < addresses.length; i++) {
        if (primaryAddress && addressWithKYCStatus) {
            break;
        }
        const { kycStatus, isPrimary } = addresses[i];
        if (kycStatus && !addressWithKYCStatus) {
            addressWithKYCStatus = addresses[i];
        }
        if (isPrimary && !primaryAddress) {
            primaryAddress = addresses[i];
        }
    }
    if (addressWithKYCStatus) {
        return addressWithKYCStatus;
    }
    if (primaryAddress) {
        return primaryAddress;
    }
    return undefined;
};
export const selectPrimaryAddressForGB = createSelector((state) => state.addressReducer.userAddresses, (addresses) => getDisplayAddressGB(addresses));
export const selectSecondaryAddresses = createSelector((state) => state.addressReducer.userAddresses, selectPrimaryAddressForGB, (addresses, primaryAddress) => {
    if (primaryAddress) {
        return addresses.filter((address) => address.id !== primaryAddress.id);
    }
    return emptyArray;
});
export const selectAddressToDisplay = createSelector(selectIsGBUser, selectPrimaryAddress, selectPrimaryAddressForGB, (isGbUser, primaryAddress, primaryAddressGB) => {
    if (isGbUser) {
        return primaryAddressGB;
    }
    return primaryAddress;
});
const getUserNationalitiesStr = (nationalities) => {
    if (nationalities?.length) {
        return nationalities.reduce((acc, curr, index) => {
            const country = lookup.byIso(curr)?.country || curr;
            return index === 0 ? country : `${acc}, ${country}`;
        }, '');
    }
    return '';
};
export const selectUserNationalities = createSelector((store) => store.user.user, (user) => {
    const nationalitiesStr = getUserNationalitiesStr(user.nationalities);
    const citizenshipStr = !nationalitiesStr && user.citizenship
        ? getUserNationalitiesStr([user.citizenship])
        : '';
    return nationalitiesStr || citizenshipStr;
});
export const selectHasUSCitizenship = createSelector((store) => store.user.user, (user) => {
    if (user.nationalities?.length) {
        return user.nationalities.includes('USA');
    }
    return false;
});
export const selectSavingsPlanConnectionId = createSelector(selectConnections, (store) => store.pots.savingsPlan?.bankId, (store) => store.pots.savingsPlan?.bankInfo?.accountId, (connections, bankId, savingsPlanAccountId) => {
    let savingsPlanConnectionId;
    if (connections?.length && bankId && savingsPlanAccountId) {
        for (let i = 0; i < connections.length; i++) {
            const connectionI = connections[i];
            if (connectionI.bankId === bankId) {
                const bankAcc = connectionI.accounts.find((account) => account.id === savingsPlanAccountId);
                if (bankAcc) {
                    savingsPlanConnectionId = connectionI.id;
                    break;
                }
            }
        }
    }
    return savingsPlanConnectionId;
});
const findErrorToDisplay = (errorsData) => errorsData && errorsData.find((error) => error && error.errorDisplayModal);
const findSpecificError = (errorType, errorsData) => errorsData &&
    errorsData.find((error) => error && error.errorType === errorType);
export const selectErrorToDisplay = createSelector((store) => store.error.errorsData, (errorsData) => {
    if (errorsData?.length) {
        const noNetworkError = findSpecificError('FetchError', errorsData);
        if (noNetworkError) {
            log(noNetworkError);
        }
        else {
            // Handles showModal flags for each errors
            const errorToDisplay = findErrorToDisplay(errorsData);
            return errorToDisplay;
        }
    }
    return undefined;
});
export const selectAnalyticsPosition = createNonMemoizedSelector([
    (store) => store.expenses.analyticsPosition,
    (store) => store.expenses.budgetingPosition,
    (_, type) => type,
], (analyticsPosition, budgetingPosition, type) => {
    if (type === 'analytics') {
        return analyticsPosition;
    }
    return budgetingPosition;
});
export const selectDebtPieChartSlices = createSelector([selectAccountsSeparatedDebt, selectDefaultZeroAmount], (accounts, zero) => {
    const data = [];
    if (accounts.CREDITCARD?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: 'Credit Cards',
            colorKey: overviewColorMap.Debt,
            value: accounts.CREDITCARD.negativeTotal,
            category: {
                name: 'Credit Cards',
                currency: accounts.CREDITCARD.array[0]?.currency ?? zero?.currency,
            },
        });
    }
    if (accounts.LOAN?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: 'Loan',
            colorKey: overviewColorMap.Debt,
            value: accounts.LOAN.negativeTotal,
            category: {
                name: 'Loan',
                currency: accounts.LOAN.array[0]?.currency ?? zero.currency,
            },
        });
    }
    if (accounts.MORTGAGE?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: 'Mortgage',
            colorKey: overviewColorMap.Debt,
            value: accounts.MORTGAGE.negativeTotal,
            category: {
                name: 'Mortgage',
                currency: accounts.MORTGAGE.array[0]?.currency ?? zero.currency,
            },
        });
    }
    if (accounts.CHECKING?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: i18n.t('current'),
            colorKey: overviewColorMap.Debt,
            value: accounts.CHECKING.negativeTotal,
            category: {
                name: i18n.t('current'),
                currency: accounts.CHECKING.array[0]?.currency ?? zero.currency,
            },
        });
    }
    if (accounts.SAVINGS?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: 'Savings',
            colorKey: overviewColorMap.Debt,
            value: accounts.SAVINGS.negativeTotal,
            category: {
                name: 'Savings',
                currency: accounts.SAVINGS.array[0]?.currency ?? zero.currency,
            },
        });
    }
    if (accounts.ALL_INVESTMENT?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: 'Investments',
            colorKey: overviewColorMap.Debt,
            value: accounts.ALL_INVESTMENT.negativeTotal,
            category: {
                name: 'Investments',
                currency: accounts.ALL_INVESTMENT.array[0]?.currency ?? zero.currency,
            },
        });
    }
    if (accounts.PENSION?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: 'Pensions',
            colorKey: overviewColorMap.Debt,
            value: accounts.PENSION.negativeTotal,
            category: {
                name: 'Pensions',
                currency: accounts.PENSION.array[0]?.currency ?? zero.currency,
            },
        });
    }
    if (accounts.REAL_ESTATE?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: 'Real Estate',
            colorKey: overviewColorMap.Debt,
            value: accounts.REAL_ESTATE.negativeTotal,
            category: {
                name: 'Real Estate',
                currency: accounts.REAL_ESTATE.array[0]?.currency ?? zero.currency,
            },
        });
    }
    if (accounts.VEHICLE?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: 'Vehicles',
            colorKey: overviewColorMap.Debt,
            value: accounts.VEHICLE.negativeTotal,
            category: {
                name: 'Vehicles',
                currency: accounts.VEHICLE.array[0]?.currency ?? zero.currency,
            },
        });
    }
    if (accounts.OTHER?.negativeTotal) {
        data.push({
            hideIcon: true,
            key: 'Other assets',
            colorKey: overviewColorMap.Debt,
            value: accounts.OTHER.negativeTotal,
            category: {
                name: 'Other assets',
                currency: accounts.OTHER.array[0]?.currency ?? zero.currency,
            },
        });
    }
    return data.sort((a, b) => a.value - b.value);
});
export const selectAssetsPieChartSlices = createSelector([
    selectAccountsSeparatedDebt,
    selectAllCurrentAccounts,
    selectAllSavingAccounts,
    selectAllInvestmentWithCryptoAccounts,
    selectDefaultZeroAmount,
], (accounts, allCurrentAccounts, allSavingsAccounts, allInvestmentAccounts, zero) => {
    const data = [];
    if (allCurrentAccounts.positiveTotal) {
        data.push({
            hideIcon: true,
            key: i18n.t('current'),
            colorKey: overviewColorMap.Checking,
            value: allCurrentAccounts.positiveTotal,
            category: {
                name: i18n.t('current'),
                currency: allCurrentAccounts.currency,
            },
        });
    }
    if (allSavingsAccounts.positiveTotal) {
        data.push({
            hideIcon: true,
            key: 'Savings',
            colorKey: overviewColorMap.Savings,
            value: allSavingsAccounts.positiveTotal,
            category: {
                name: 'Savings',
                currency: allSavingsAccounts.currency,
            },
        });
    }
    if (allInvestmentAccounts.positiveTotal) {
        data.push({
            hideIcon: true,
            key: 'Investments',
            colorKey: overviewColorMap.Investments,
            value: allInvestmentAccounts.positiveTotal,
            category: {
                name: 'Investments',
                currency: allInvestmentAccounts.currency,
            },
        });
    }
    if (accounts.PENSION?.positiveTotal) {
        data.push({
            hideIcon: true,
            key: 'Pensions',
            colorKey: 'secondary',
            value: accounts.PENSION.positiveTotal,
            category: {
                name: 'Pensions',
                currency: accounts.PENSION.array[0]?.currency ?? zero?.currency,
            },
        });
    }
    if (accounts.REAL_ESTATE?.positiveTotal) {
        data.push({
            hideIcon: true,
            key: 'Real Estate',
            colorKey: 'secondary',
            value: accounts.REAL_ESTATE.positiveTotal,
            category: {
                name: 'Real Estate',
                currency: accounts.REAL_ESTATE.array[0]?.currency ?? zero?.currency,
            },
        });
    }
    if (accounts.VEHICLE?.positiveTotal) {
        data.push({
            hideIcon: true,
            key: 'Vehicles',
            colorKey: 'secondary',
            value: accounts.VEHICLE.positiveTotal,
            category: {
                name: 'Vehicles',
                currency: accounts.VEHICLE.array[0]?.currency ?? zero?.currency,
            },
        });
    }
    if (accounts.OTHER?.positiveTotal) {
        data.push({
            hideIcon: true,
            key: 'Other assets',
            colorKey: 'secondary',
            value: accounts.OTHER.positiveTotal,
            category: {
                name: 'Other assets',
                currency: accounts.OTHER.array[0]?.currency ?? zero?.currency,
            },
        });
    }
    if (accounts.LOAN?.positiveTotal) {
        data.push({
            hideIcon: true,
            key: 'Loans',
            colorKey: overviewColorMap.Investments,
            value: accounts.LOAN.positiveTotal,
            category: {
                name: 'Loans',
                currency: accounts.LOAN.array[0]?.currency ?? zero?.currency,
            },
        });
    }
    if (accounts.CREDITCARD?.positiveTotal) {
        data.push({
            hideIcon: true,
            key: 'Credit Cards',
            colorKey: overviewColorMap.Investments,
            value: accounts.CREDITCARD.positiveTotal,
            category: {
                name: 'Credit Cards',
                currency: accounts.CREDITCARD.array[0]?.currency ?? zero?.currency,
            },
        });
    }
    return data.sort((a, b) => b.value - a.value);
});
export const selectSectionedAccounts = createSelector([
    (_state, title, isPieChartSelected) => {
        if (!isPieChartSelected) {
            return undefined;
        }
        return title;
    },
    selectAllSavingAccounts,
    selectAllInvestmentWithCryptoAccounts,
    selectDebtPieChartSlices,
    selectAssetsPieChartSlices,
], (title, savingsAccounts, investmentAccounts, debtPieSlices, assetsPieChartSlices) => {
    if (title === undefined) {
        return emptyArray;
    }
    switch (title) {
        case 'Savings': {
            const data = [];
            savingsAccounts.array.forEach((item) => {
                const [balance] = getBalance(item, false);
                if (!balance?.amount ||
                    ('proPreviewAccount' in item && item.proPreviewAccount) ||
                    ('ultimatePreviewAccount' in item && item.ultimatePreviewAccount) ||
                    ('deactivatedOverQuota' in item && item.deactivatedOverQuota)) {
                    return;
                }
                const { currency } = savingsAccounts;
                data.push({
                    hideIcon: true,
                    key: String(item.id),
                    value: balance.amount,
                    category: {
                        currency,
                        name: item.name,
                    },
                });
            });
            return data;
        }
        case 'Investments': {
            const data = [];
            investmentAccounts.array.forEach((item) => {
                const [balance] = getBalance(item, false);
                if (!balance?.amount ||
                    ('proPreviewAccount' in item && item.proPreviewAccount) ||
                    ('ultimatePreviewAccount' in item && item.ultimatePreviewAccount) ||
                    ('deactivatedOverQuota' in item && item.deactivatedOverQuota)) {
                    return;
                }
                const { currency } = investmentAccounts;
                data.push({
                    hideIcon: true,
                    key: String(item.id),
                    value: balance.amount,
                    category: {
                        currency,
                        name: item.name,
                    },
                });
            });
            return data;
        }
        case 'Net Worth - Debt': {
            return debtPieSlices;
        }
        case 'Net Worth - Asset': {
            return assetsPieChartSlices;
        }
        default:
            return emptyArray;
    }
});
const emptyAllBalance = [];
export const selectAllNetWorthBalance = createSelector([
    (state) => state.balanceHistory.networthAll,
    (state) => state.balanceHistory.assetAll,
    (state) => state.balanceHistory.debtAll,
    selectDefaultCurrency,
], (networthBalanceHistory, assetsBalanceHistory, debtBalanceHistory, defaultCurrency) => {
    if (!networthBalanceHistory?.length ||
        !assetsBalanceHistory?.length ||
        !debtBalanceHistory?.length) {
        return emptyAllBalance;
    }
    const balanceHistory = [];
    if (networthBalanceHistory.length === assetsBalanceHistory.length &&
        networthBalanceHistory.length === debtBalanceHistory.length) {
        if (!(networthBalanceHistory[0].timestamp.substring(0, 10) ===
            assetsBalanceHistory[0].timestamp.substring(0, 10) &&
            networthBalanceHistory[0].timestamp.substring(0, 10) ===
                debtBalanceHistory[0].timestamp.substring(0, 10))) {
            return emptyAllBalance;
        }
        for (let i = 0; i < networthBalanceHistory.length; i++) {
            balanceHistory.push({
                currency: defaultCurrency,
                timestamp: networthBalanceHistory[i].timestamp,
                balance: networthBalanceHistory[i].balance,
                assetBalance: assetsBalanceHistory[i].balance,
                debtBalance: debtBalanceHistory[i].balance,
            });
        }
    }
    return balanceHistory;
});
const emptyAllAssetBalance = [];
export const selectAllAssetBalanceHistory = createSelector([
    (state) => state.balanceHistory.debtAll,
    (state) => state.balanceHistory.assetAll,
    (state) => state.balanceHistory.networthAll,
    selectDefaultCurrency,
], (debtBalanceHistory, assetsBalanceHistory, networthBalanceHistory, defaultCurrency) => {
    if (!networthBalanceHistory?.length ||
        !assetsBalanceHistory?.length ||
        !debtBalanceHistory?.length) {
        return emptyAllAssetBalance;
    }
    const balanceHistory = [];
    if (networthBalanceHistory.length === assetsBalanceHistory.length &&
        networthBalanceHistory.length === debtBalanceHistory.length) {
        if (!(networthBalanceHistory[0].timestamp.substring(0, 10) ===
            debtBalanceHistory[0].timestamp.substring(0, 10) &&
            networthBalanceHistory[0].timestamp.substring(0, 10) ===
                assetsBalanceHistory[0].timestamp.substring(0, 10))) {
            return emptyAllAssetBalance;
        }
        for (let i = 0; i < assetsBalanceHistory.length; i++) {
            const assetBalance = assetsBalanceHistory[i].balance;
            balanceHistory.push({
                currency: defaultCurrency,
                timestamp: assetsBalanceHistory[i].timestamp,
                balance: assetBalance,
                assetBalance,
                debtBalance: debtBalanceHistory[i].balance,
                networthBalance: networthBalanceHistory[i].balance,
                breakdown: assetsBalanceHistory[i].breakdown,
            });
        }
    }
    return balanceHistory;
});
export const selectALLBalanceHistory = createSelector([
    (_, overviewSection) => overviewSection,
    (_, __, isAssetsVsDebt) => isAssetsVsDebt,
    (state) => state.balanceHistory.everydayAll,
    (state) => state.balanceHistory.savingsAll,
    (state) => state.balanceHistory.investmentAll,
    (state) => state.balanceHistory.debtAll,
    selectAllNetWorthBalance,
    selectAllAssetBalanceHistory,
], (overviewSection, isAssetsVsDebt, everydayAll, savingsAll, investmentAll, debtAll, allNetworthBalance, assetsAll) => {
    const currentYear = new Date().getFullYear();
    switch (overviewSection) {
        case 'Everyday': {
            if (!everydayAll?.length) {
                return [emptyAllBalance];
            }
            return [
                everydayAll.map((dataPoint) => {
                    const breakdown = typeof dataPoint.breakdown?.current === 'number' &&
                        typeof dataPoint.breakdown?.creditCard === 'number'
                        ? {
                            current: formatAmount(dataPoint.breakdown.current, dataPoint.currency, 2),
                            creditCard: formatAmount(dataPoint.breakdown.creditCard, dataPoint.currency, 2),
                        }
                        : undefined;
                    return {
                        ...dataPoint,
                        breakdown,
                        formattedPrice: formatAmount(dataPoint.balance, dataPoint.currency, 2),
                        label: formatChartTimestamp(dataPoint.timestamp, currentYear),
                    };
                }),
            ];
        }
        case 'Savings': {
            if (!savingsAll?.length) {
                return [emptyAllBalance];
            }
            return [
                savingsAll.map((dataPoint) => ({
                    ...dataPoint,
                    formattedPrice: formatAmount(dataPoint.balance, dataPoint.currency, 2),
                    label: formatChartTimestamp(dataPoint.timestamp, currentYear),
                    breakdown: undefined,
                })),
            ];
        }
        case 'Investments': {
            if (!investmentAll?.length) {
                return [emptyAllBalance];
            }
            return [
                investmentAll.map((dataPoint) => ({
                    ...dataPoint,
                    formattedPrice: formatAmount(dataPoint.balance, dataPoint.currency, 2),
                    label: formatChartTimestamp(dataPoint.timestamp, currentYear),
                    breakdown: undefined,
                })),
            ];
        }
        case 'Net Worth - Main': {
            if (isAssetsVsDebt) {
                if (assetsAll?.length && debtAll?.length) {
                    return [
                        assetsAll.map((dataPoint) => ({
                            ...dataPoint,
                            formattedPrice: formatAmount(dataPoint.balance, dataPoint.currency, 2),
                            label: formatChartTimestamp(dataPoint.timestamp, currentYear),
                            formattedAssetAmount: 'assetBalance' in dataPoint &&
                                typeof dataPoint.assetBalance === 'number'
                                ? formatAmount(dataPoint.assetBalance, dataPoint.currency, 2)
                                : undefined,
                            formattedDebtAmount: 'debtBalance' in dataPoint &&
                                typeof dataPoint.debtBalance === 'number'
                                ? formatAmount(dataPoint.debtBalance, dataPoint.currency, 2)
                                : undefined,
                            networthAmount: 'networthBalance' in dataPoint &&
                                typeof dataPoint.networthBalance === 'number'
                                ? dataPoint.networthBalance
                                : undefined,
                            breakdown: undefined,
                        })),
                        debtAll.map((dataPoint) => ({
                            ...dataPoint,
                            formattedPrice: formatAmount(dataPoint.balance, dataPoint.currency, 2),
                            label: formatChartTimestamp(dataPoint.timestamp, currentYear),
                            breakdown: undefined,
                        })),
                    ];
                }
                return [emptyAllBalance];
            }
            if (!allNetworthBalance?.length) {
                return [emptyAllBalance];
            }
            return [
                allNetworthBalance.map((dataPoint) => ({
                    ...dataPoint,
                    formattedPrice: formatAmount(dataPoint.balance, dataPoint.currency, 2),
                    label: formatChartTimestamp(dataPoint.timestamp, currentYear),
                    formattedAssetAmount: 'assetBalance' in dataPoint &&
                        typeof dataPoint.assetBalance === 'number'
                        ? formatAmount(dataPoint.assetBalance, dataPoint.currency, 2)
                        : undefined,
                    formattedDebtAmount: 'debtBalance' in dataPoint &&
                        typeof dataPoint.debtBalance === 'number'
                        ? formatAmount(dataPoint.debtBalance, dataPoint.currency, 2)
                        : undefined,
                    breakdown: undefined,
                })),
            ];
        }
        case 'Net Worth - Asset': {
            if (!assetsAll?.length) {
                return [emptyAllBalance];
            }
            return [
                assetsAll.map((dataPoint) => {
                    const breakdown = dataPoint.breakdown?.current ||
                        dataPoint.breakdown?.investment ||
                        dataPoint.breakdown?.savings ||
                        dataPoint.breakdown?.other
                        ? {
                            current: formatAmount(dataPoint.breakdown.current || 0, dataPoint.currency, 2),
                            investment: formatAmount(dataPoint.breakdown.investment || 0, dataPoint.currency, 2),
                            savings: formatAmount(dataPoint.breakdown.savings || 0, dataPoint.currency, 2),
                            other: formatAmount(dataPoint.breakdown.other || 0, dataPoint.currency, 2),
                        }
                        : undefined;
                    return {
                        ...dataPoint,
                        formattedPrice: formatAmount(dataPoint.balance, dataPoint.currency, 2),
                        label: formatChartTimestamp(dataPoint.timestamp, currentYear),
                        breakdown,
                    };
                }),
            ];
        }
        case 'Net Worth - Debt': {
            if (!debtAll?.length) {
                return [emptyAllBalance];
            }
            return [
                debtAll.map((dataPoint) => ({
                    ...dataPoint,
                    formattedPrice: formatAmount(dataPoint.balance, dataPoint.currency, 2),
                    label: formatChartTimestamp(dataPoint.timestamp, currentYear),
                    breakdown: formatDebtBreakdown(dataPoint.breakdown, dataPoint.currency),
                })),
            ];
        }
        default:
            return [emptyAllBalance];
    }
});
export const selectRemovePayTab = createSelector([(store) => store.utils?.featureFlags, selectIsGBUser], (featureFlags, isGBUser) => isGBUser && checkFeatureFlag(featureFlags, 'remove_pay_tab'));
export const selectShowFeedQuoteCards = createSelector([(store) => store.utils?.featureFlags, selectIsGBUser], (featureFlags, isGBUser) => isGBUser && checkFeatureFlag(featureFlags, 'quotes_in_feed'));
export const selectFeedQuoteCards = createSelector([(store) => store.switching.feedQuoteEnquiries], (enquiriesByPartner) => buildCards(Object.values(enquiriesByPartner).flat()));
export const selectNavigatorConfig = createSelector([selectIsGBUser, selectRemovePayTab], (isGBUser, removePayTab) => ({
    showSaveTab: isGBUser,
    // the remove-pay-tab experience replaces the tab with feed entry points;
    // web never shows the tab as it only ever rendered a coming-soon item
    showPayTab: isGBUser && !isWeb && !removePayTab,
    showInvestTab: isGBUser,
    showBorrowTab: isGBUser,
    showNetworthTab: !isGBUser,
    showBudgetingTab: !isGBUser,
    showRecurringTab: !isGBUser,
    showGroupTab: !isGBUser,
}));
export const selectFeedRewards = createSelector([(store) => store.rewards.rewards], (rewards) => {
    const topAndFeaturedRewards = rewards.filter(({ isFeatured, isTopOffer }) => isTopOffer || isFeatured);
    let i = 0;
    while (topAndFeaturedRewards.length < 10 && i < rewards.length) {
        const reward = rewards[i];
        if (!reward.isFeatured && !reward.isTopOffer) {
            topAndFeaturedRewards.push(reward);
        }
        i += 1;
    }
    return topAndFeaturedRewards;
});
export const selectOrderedConnections = createSelector([
    selectConnections,
    selectSavingsPlanConnectionId,
    (_, fromConnectionId) => fromConnectionId,
], (connections, savingsPlanConnectionId, fromConnectionId) => connections.filter(isBankLoginConnection).sort((a, b) => {
    if (savingsPlanConnectionId &&
        (a.id === savingsPlanConnectionId) !==
            (b.id === savingsPlanConnectionId)) {
        return a.id === savingsPlanConnectionId ? -1 : 1;
    }
    if (fromConnectionId !== undefined &&
        (a.id === fromConnectionId) !== (b.id === fromConnectionId)) {
        return a.id === fromConnectionId ? -1 : 1;
    }
    if (a.deactivatedOverQuota !== b.deactivatedOverQuota) {
        return a.deactivatedOverQuota ? 1 : -1;
    }
    const aNeedsFix = isConnectionBroken(a);
    const bNeedsFix = isConnectionBroken(b);
    if (aNeedsFix !== bNeedsFix) {
        return aNeedsFix ? 1 : -1;
    }
    if (a.bankInfo.name !== b.bankInfo.name) {
        return strcmp(a.bankInfo.name, b.bankInfo.name);
    }
    if (a.lastSuccessfulSync && b.lastSuccessfulSync) {
        return strcmp(b.lastSuccessfulSync, a.lastSuccessfulSync);
    }
    return 0;
}));
export const selectFilledExtraMemberSeats = createSelector([(store) => store.sharedPlans.extraMembersInfo], (extraMembersInfo) => extraMembersInfo?.seats.filter((seat) => seat.member));
